import React, { useState } from "react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/constants";
import { PhoneCall, Copy, Check, TrendingDown, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const buildScript = (d) => `Hi, my name is [Your Name] and I'm calling about my ${d.name} account ending in [last 4 digits].

I've been a customer for [X years] and I've made my payments on time. I'm currently paying ${d.interest_rate}% APR, and I'd like to request a lower interest rate on this account.

Are you able to reduce my APR today?

— If they say they can't:
I understand. What would I need to qualify for a lower rate? And could you connect me with the retention or loyalty department to review my account? I've received 0% balance-transfer offers, but I'd prefer to stay with you if we can bring this rate down.

— If they offer a reduction:
Thank you. Can you confirm the new APR and the exact date it takes effect, and send that to me in writing or by email?

Thanks very much for your help.`;

const TIPS = [
  "Call the number on the back of your card and ask specifically for a rate reduction.",
  "Lead with your on-time payment history and how long you've been a customer.",
  "Mention competing 0% balance-transfer offers — retention teams can often match or lower your rate.",
  "If the first rep says no, politely ask for a supervisor or the retention department.",
  "Get the new rate and effective date confirmed in writing, then log it below.",
];

export default function AprNegotiation({ debts, onSaved }) {
  const targets = (debts || [])
    .filter(d => d.balance > 0 && (d.interest_rate || 0) > 0 && !d.promo_active)
    .sort((a, b) => b.interest_rate - a.interest_rate);
  const [openId, setOpenId] = useState(targets[0]?.id || null);
  const [copied, setCopied] = useState(false);
  const [newApr, setNewApr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!targets.length) return null;

  const copy = async (d) => {
    try { await navigator.clipboard.writeText(buildScript(d)); setCopied(true); toast.success("Script copied"); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  const logApr = async (d) => {
    const v = parseFloat(newApr);
    if (isNaN(v) || v < 0) { toast.error("Enter the new APR"); return; }
    setBusy(true);
    try { await api.patch(`/debts/${d.id}/apr`, { interest_rate: v }); toast.success(`${d.name} APR updated to ${v}%`, { description: "Your payoff plan just got recalculated." }); setNewApr(""); onSaved?.(); }
    catch { toast.error("Could not update APR"); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="apr-negotiation">
      <div className="p-4 border-b border-border flex items-center gap-2">
        <div className="h-9 w-9 rounded-lg bg-amber-500/15 text-amber-500 flex items-center justify-center"><PhoneCall className="h-4 w-4" /></div>
        <div>
          <div className="font-display font-bold text-lg">Lower your APR with a phone call</div>
          <div className="text-xs text-muted-foreground">A 10-minute call can cut your rate — often more than months of extra payments. Start with your highest rate.</div>
        </div>
      </div>
      <ul className="divide-y divide-border/60">
        {targets.map(d => {
          const open = openId === d.id;
          return (
            <li key={d.id} data-testid={`apr-item-${d.id}`}>
              <button onClick={() => setOpenId(open ? null : d.id)} data-testid={`apr-toggle-${d.id}`}
                className="w-full p-4 flex items-center gap-3 hover:bg-accent/40 transition text-left">
                <TrendingDown className="h-4 w-4 text-amber-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{d.name}</div>
                  <div className="text-[11px] text-muted-foreground">{fmt(d.balance)} balance · {d.interest_rate}% APR</div>
                </div>
                <span className="font-mono font-bold text-amber-500">{d.interest_rate}%</span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="px-4 pb-4 space-y-3" data-testid={`apr-panel-${d.id}`}>
                  <div className="relative">
                    <pre className="whitespace-pre-wrap text-xs leading-relaxed p-3 rounded-xl bg-background border border-border font-sans" data-testid={`apr-script-${d.id}`}>{buildScript(d)}</pre>
                    <button onClick={() => copy(d)} data-testid={`apr-copy-${d.id}`}
                      className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-card border border-border text-[11px] font-semibold hover:bg-accent">
                      {copied ? <><Check className="h-3 w-3 text-emerald-500" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                    </button>
                  </div>
                  <ul className="text-[11px] text-muted-foreground space-y-1 list-disc pl-4">
                    {TIPS.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                  <div className="flex items-center gap-2 flex-wrap pt-1">
                    <span className="text-xs font-semibold">Got a lower rate?</span>
                    <div className="relative">
                      <input type="number" min="0" step="0.1" value={newApr} onChange={e => setNewApr(e.target.value)} placeholder="New APR" data-testid={`apr-input-${d.id}`}
                        className="w-24 pr-6 pl-2 py-1.5 rounded-lg bg-background border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500/40" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                    </div>
                    <button onClick={() => logApr(d)} disabled={busy} data-testid={`apr-save-${d.id}`}
                      className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 transition disabled:opacity-50">Log new APR</button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
